import maUploadDocument from '@/modules/Document/views/handleDocumentContainer/components/documentUploadStep'
import maActionsButtons from '@/components/button-blue'
import maModal from '@/components/modal'
import maSpinner from '@/components/spinner'
import maNavbarStepper from '@/components/navbarStepper'
import { mapState, mapMutations } from 'vuex'
import flowMixin from '@/modules/Flow/mixins/flowMixin'
import {
  billmaApiPostFile,
  billmaApiGetFile
} from '@/modules/Document/services/file'
import { billmaApiGetDocuments } from '@/modules/Document/services/document'
import { billmaApiPutClaim, billmaChangeE2EFlow } from '@/modules/Claim/services/sinister'
import { UPDATE_LOADER, FLOW_MODULE_NAME } from '@/store/types'
import downloadFileFromFetchRequest from '@/utils/downloadFileFromFetchRequest'
// import { formatAmount } from '@/utils/amounts'
import { BILLMA_DOCUMENT_ICR_STATUS, BILLMA_DOCUMENTS_TYPES } from '@/constants'
import { FLOW_TRANSITIONS } from '@/modules/Flow/constants'
import { prepareErrorForSentry } from '@/utils/errorHandling'

const mapDocuments = (documentsApiData) => {
  return documentsApiData.map((document) => {
    return {
      id: document.file.createdName,
      name: document.file.name,
      canBeDeleted: !document.processStatus
    }
  })
}

const icrTextChangeTimeout = 4000

export default {
  name: 'handleDocumentContainer',

  mixins: [flowMixin],

  data() {
    return {
      uploadLoading: false,
      icrLoading: false,
      icrProcessingModalTextActive: 'one',
      handleDocumentStepActive: 'uploadDocuments',
      processedClaimDocuments: [],
      toProcessClaimDocuments: [],
      claimId: null,
      showGeneralValue: true,
      showPrivacy: false,
      isChecked: false
    }
  },

  computed: {
    ...mapState(FLOW_MODULE_NAME, {
      stateFlowViewOptions: 'flowViewOptions'
    }),
    primaryButtonIsDisabled() {
      return this.mappedDocuments.length === 0 || this.icrLoading
    },
    mappedDocuments() {
      const mappedClaimDocuments = mapDocuments([
        ...this.processedClaimDocuments,
        ...this.toProcessClaimDocuments
      ])

      return mappedClaimDocuments
    },
    icrIsProcessing() {
      return this.processedClaimDocuments.some(
        (document) =>
          document.processStatus === BILLMA_DOCUMENT_ICR_STATUS.PROCESSING ||
          document.processStatus === BILLMA_DOCUMENT_ICR_STATUS.PENDING
      )
    },
    isFrais() {
      return (
        this.stateFlowViewOptions.smart_values.frais.compensation_base !== 0
      )
    },
    isMobilier() {
      if (this.stateFlowViewOptions.smart_values.mobilier) return true
    }
  },

  components: {
    maUploadDocument,
    maActionsButtons,
    maModal,
    maSpinner,
    maNavbarStepper
  },

  methods: {
    ...mapMutations({
      updateLoader: UPDATE_LOADER
    }),

    async uploadFile(file) {
      try {
        const fileData = await billmaApiPostFile({
          fileBlob: file,
          sinisterId: this.getSinisterId,
          documentType: BILLMA_DOCUMENTS_TYPES.BUDGET // In this version, BUDGET is the value by default.
        })

        this.toProcessClaimDocuments.push({ file: { ...fileData } })

        return fileData
      } catch (error) {
        const context = {
          component: 'handleDocumentContainer',
          method: 'uploadFile',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context, { willRedirect: true })

        this.sendSentryError(
          `Error sending document to billma: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          { ...tags, uploadFileName: file.name },
          this.getSinisterId,
          'uploadFile'
        )

        this.goToErrorView()
      }
    },

    async setClaimDocuments() {
      try {
        const { claimDtoList } = await billmaApiGetDocuments(this.getSinisterId)
        if (claimDtoList.length > 0) {
          this.claimId = claimDtoList[0].claimId
          this.processedClaimDocuments = claimDtoList
        }
      } catch (error) {
        const context = {
          component: 'handleDocumentContainer',
          method: 'setClaimDocuments',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context, { willRedirect: true })

        this.sendSentryError(
          `Error getting documents from billma: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'setClaimDocuments'
        )

        this.goToErrorView()
      }
    },

    async triggerIcr() {
      try {
        const claimExistsInBillma = this.claimId
        // If claim/siniste doesn't exist in Billma, we have to set the sinisterID to 0 because of api requirement
        await billmaApiPutClaim({
          sinisterId: claimExistsInBillma ? this.claimId : 0,
          externalId: this.getSinisterId,
          documents: this.toProcessClaimDocuments
        })
      } catch (error) {
        const context = {
          component: 'handleDocumentContainer',
          method: 'triggerIcr',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context, { willRedirect: true })

        this.sendSentryError(
          `Error sending claim to billma: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'triggerIcr'
        )

        this.goToErrorView()
      }
    },

    async checkIfIcrIsprocessing(promiseResolve) {
      await this.setClaimDocuments()
      if (this.icrIsProcessing) {
        setTimeout(() => {
          this.checkIfIcrIsprocessing(promiseResolve)
        }, 2000)
      } else {
        promiseResolve()
      }
    },

    handleIcrProcessing() {
      return new Promise((resolve) => {
        this.checkIfIcrIsprocessing(resolve)
      })
    },

    handleIcrResult() {
      if (this.isMobilier) {
        this.goToNextState(FLOW_TRANSITIONS.SHOW_MOBILIER)
      } else if (this.isFrais) {
        this.goToNextState(FLOW_TRANSITIONS.SHOW_FRAIS)
      } else {
        this.goToNextState(FLOW_TRANSITIONS.SHOW_RESUME)
      }
    },

    handleIcrProcessingTextChange() {
      if (!this.icrLoading) return
      if (this.icrProcessingModalTextActive === 'one') {
        this.icrProcessingModalTextActive = 'two'
      } else {
        this.icrProcessingModalTextActive = 'one'
      }
      setTimeout(() => {
        this.handleIcrProcessingTextChange()
      }, icrTextChangeTimeout)
    },

    async handleIcrOnMounted() {
      if (this.icrIsProcessing) {
        this.icrLoading = true
        this.$refs.icrProcessingModal.showModal = true
        // setTimeout(() => {
        //   this.handleIcrProcessingTextChange()
        // }, icrTextChangeTimeout)
        await this.handleIcrProcessing()
        this.icrLoading = false
        this.$refs.icrProcessingModal.showModal = false
        this.handleIcrResult()
      }
    },

    async handleDocumentInputChange(file) {
      this.uploadLoading = true
      await this.uploadFile(file)
      this.uploadLoading = false
    },

    async handlePrimaryButtonClick() {
      this.icrLoading = true
      this.$refs.icrProcessingModal.showModal = true
      // setTimeout(() => {
      //   this.handleIcrProcessingTextChange()
      // }, icrTextChangeTimeout)

      try {
        await this.triggerIcr()
        this.toProcessClaimDocuments = []
        this.icrProcessingModalTextActive = 'two'
        await this.handleIcrProcessing()
        this.icrLoading = false
        this.$refs.icrProcessingModal.showModal = false
        this.handleIcrResult()
      } catch {
        this.goToNextState(FLOW_TRANSITIONS.FINISHED_ERROR)
      }
    },

    async handleSecondaryButtonClick() {
      await billmaChangeE2EFlow(this.getSinisterId, 'immobilier')
      this.goToNextState(FLOW_TRANSITIONS.CALLBACK_REQUEST)
    },

    handleTertiaryButtonClick() {
      this.goToNextState(FLOW_TRANSITIONS.SHOW_COMPENSATION_TYPE)
    },

    handleDocumentDeleteClick(filename) {
      this.toProcessClaimDocuments = this.toProcessClaimDocuments.filter(
        (document) => document.file.createdName !== filename
      )
    },

    async handleDocumentDownloadClick({ documentId, documentName }) {
      try {
        const fileData = await billmaApiGetFile(documentId)
        downloadFileFromFetchRequest(fileData, documentName)
      } catch (error) {
        const context = {
          component: 'handleDocumentContainer',
          method: 'handleDocumentDownloadClick',
          route: this.$route.path
        }

        const tags = prepareErrorForSentry(error, context, { willRedirect: true })

        this.sendSentryError(
          `Error downloading document from billma: ${error.message || 'Unknown error'}`,
          error.response && error.response.status >= 500 ? 'error' : 'warning',
          tags,
          this.getSinisterId,
          'handleDocumentDownloadClick'
        )

        this.goToErrorView()
      }
    },

    showPrivacyPolicy() {
      this.showPrivacy = true
      this.showGeneralValue = false
    },

    hidePrivacyPolicy () {
      this.showPrivacy = false
      this.showGeneralValue = true
    },

    closeModal () {
      this.$refs.modalCookiesAndLegal.showModal = false
      localStorage.setItem('t&cAccept', 'true')
    },

    goToPrivacy () {
      const routeData = this.$router.resolve({
        name: 'privacyPolicy',
        query: { isPrivacy: true }
      })
      window.open(routeData.href, '_blank')
    }
  },

  async mounted() {
    if (this.$route?.params?.query?.isPrivacy) this.$router.push({ name: 'privacyPolicy' })
    this.updateLoader(true)
    await this.setClaimDocuments()
    await this.handleIcrOnMounted()
    this.updateLoader(false)

    if (this.getFlowName === 'billma_bpce') {
      this.$refs.modalCookiesAndLegal.showModal = !(localStorage.getItem('t&cAccept') && localStorage.getItem('t&cAccept') === 'true')
    }
  }
}
